import React from "react";
import { Link } from "react-router-dom";
import { FileText } from "react-feather";
import StatusBadge from "../../../../components/common/StatusBadge";
import InfoTile from "../../../../components/common/InfoTile";
import { MAROON_TINT } from "../../../../components/common/brandColors";
import CollapsibleCard, { PANEL_ACCENT } from "../../components/CollapsibleCard";
import { QUOTATION_STATUS_CLASS, QUOTATION_STATUS_LABEL } from "../quotationStatusMaps";
import { paths } from "../../../../routes/paths";

// Read-only summary + line-item breakdown of a Quotation (module: src/Quotations)
// that's linked to whatever document is being viewed - originally built inline
// on LpoDetail.jsx (an LPO created from an approved Quotation), extracted here
// so EnquiryDetail.jsx can show the same thing for a Quotation linked back to
// a Sales Enquiry via SalesEnquiryRecordGuid, without duplicating the markup.
// `quotation` must be a full QuotationDetailRecord (has `.lines`), not the
// lighter list-row shape.
// `bare` drops the outer card wrapper (LpoDetail's LinkedDocumentsPanel
// already supplies its own collapsible card chrome around this content) -
// defaults to false so EnquiryDetail.jsx's always-visible usage is unchanged.
const QuotationLinkedPanel = ({ quotation, bare = false }) => {
  if (!quotation) return null;

  const statusBadge = <StatusBadge status={quotation.status} classMap={QUOTATION_STATUS_CLASS} labelMap={QUOTATION_STATUS_LABEL} />;
  const openButton = (
    <Link to={paths.quotationDetail(quotation.recordGuid)} className="btn btn-sm btn-outline-primary">
      Open Quotation
    </Link>
  );

  // In the collapsible version (below) the title/status/Open button live in
  // the panel header, so the body skips its own heading row.
  const renderBody = (withHeading) => (
    <>
      {withHeading && (
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0 d-inline-flex align-items-center gap-2">
            <FileText size={18} /> Linked Quotation - {quotation.quotationNumber} {statusBadge}
          </h5>
          {openButton}
        </div>
      )}
      <div className="row g-2 mb-3">
        <div className="col-6 col-md-3"><InfoTile label="Customer" value={quotation.customerName} tone="maroon" /></div>
        <div className="col-6 col-md-3"><InfoTile label="Date" value={new Date(quotation.quotationDate).toLocaleDateString()} /></div>
        {quotation.approvedByName && (
          <div className="col-12 col-md-6">
            <InfoTile label="Approved By" value={`${quotation.approvedByName} on ${new Date(quotation.approvedDate).toLocaleString()}`} tone="success" />
          </div>
        )}
      </div>
      <div className="table-responsive">
        <table className="table table-sm table-bordered mb-0">
          <thead>
            <tr style={{ background: MAROON_TINT }}>
              <th>#</th><th>Description</th><th>Unit</th><th className="text-end">Qty</th><th className="text-end">Rate</th><th className="text-end">VAT %</th><th className="text-end">Amount</th>
            </tr>
          </thead>
          <tbody>
            {quotation.lines.map((l, idx) => (
              <tr key={l.lineId}>
                <td>{idx + 1}</td>
                <td>{l.description}</td>
                <td>{l.unit || "-"}</td>
                <td className="text-end">{l.qty}</td>
                <td className="text-end">{Number(l.rate).toFixed(2)}</td>
                <td className="text-end">{l.vatPercent}</td>
                <td className="text-end">{Number(l.lineAmount).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="d-flex justify-content-end gap-4 mt-2 p-2 rounded" style={{ background: MAROON_TINT }}>
        <div>Sub Total: <strong>{Number(quotation.subTotal).toFixed(2)}</strong></div>
        {quotation.discountAmount > 0 && (
          <div className="text-danger">
            Discount{quotation.discountPercent ? ` (${Number(quotation.discountPercent)}%)` : ""}:{" "}
            <strong>-{Number(quotation.discountAmount).toFixed(2)}</strong>
          </div>
        )}
        <div>VAT: <strong>{Number(quotation.vatAmount).toFixed(2)}</strong></div>
        <div className="text-primary">Total: <strong>{Number(quotation.totalAmount).toFixed(2)}</strong></div>
      </div>
    </>
  );

  if (bare) return renderBody(true);

  // Same colour-coded collapsible panel as its Enquiry-detail neighbours
  // (2026-09-12 beautification), document/maroon accent.
  return (
    <CollapsibleCard
      title={`Linked Quotation · ${quotation.quotationNumber}`}
      icon={FileText}
      accent={PANEL_ACCENT.document}
      count={quotation.lines.length}
      summary={`Total ${Number(quotation.totalAmount).toFixed(2)}`}
      actions={<>{statusBadge}{openButton}</>}
    >
      {renderBody(false)}
    </CollapsibleCard>
  );
};

export default QuotationLinkedPanel;
